import React from "react";

/**
 * The platform's confirmation dialog. Used by Contacts, Sent Invitations,
 * Banned Agents, My Club, Folder Detail, the tender cards and the pitch
 * surfaces.
 *
 * EXTENDED, not replaced, for View Pitches V2 (Marin, 2026-08-01: "Wir
 * erweitern, wir bauen nichts um was funktioniert"). With no new props it
 * renders exactly what it rendered before, so every existing caller is
 * untouched. `variant="v2"` opts a caller into the three things the inventory
 * measured as broken on a phone (5.7):
 *
 *   * the overlay had NO padding, so at 375px the panel touched both screen
 *     edges and the buttons sat 24px from them;
 *   * the two buttons were half-width blocks pushed apart by
 *     `justify-between` rather than a pair of centred, tappable buttons;
 *   * they were 42px tall, under the 44px this platform's touch targets use.
 *
 * `lines` and `cancelText` are additive for both variants: `lines` renders the
 * extra sentences a dialog wants below its headline instead of forcing callers
 * to pack block-level spans into `message`.
 *
 * `children` (additive, QAV5 custom-club-role): an optional body rendered
 * between `lines` and the button row, for the rare dialog that needs a form
 * control (e.g. a name input) rather than plain sentences. No existing caller
 * passes it, so every current render is byte-identical.
 *
 * `confirmDisabled` (additive, same change): greys out and disables the
 * confirm button without touching `loading`, e.g. while a required field is
 * still empty.
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  lines = [],
  children,
  confirmText,
  cancelText = "Cancel",
  confirmButtonClass,
  loading = false,
  confirmDisabled = false,
  variant = "default",
}) => {
  if (!isOpen) return null;

  const isV2 = variant === "v2";
  const extraLines = (lines || []).filter(Boolean);

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
        isV2 ? "p-4" : ""
      }`}
    >
      <div
        className={
          isV2
            ? "bg-white rounded-[14px] w-full max-w-[520px] p-6 shadow-[0_24px_48px_rgba(17,24,39,0.2)]"
            : "bg-white rounded-lg w-full max-w-xl p-6"
        }
      >
        {isV2 && title ? (
          <h3 className="m-0 mb-3 text-[18px] font-bold text-[#111827]">{title}</h3>
        ) : null}

        {/* {!isV2 && <h3 className="text-lg font-semibold mb-2 text-center">{title}</h3>} */}
        {/* The lead sentence is optional. The accept-success dialog has a title
            and two EQUALLY weighted sentences (design handoff), and an empty
            emphasised paragraph would still claim its margin above them. */}
        {message ? (
          <p
            className={
              isV2
                ? "m-0 mb-2 text-[14px] font-semibold leading-relaxed text-[#111827]"
                : "text-[#1A212F] text-center text-xl font-bold mb-6"
            }
          >
            {message}
          </p>
        ) : null}

        {extraLines.map((line, index) => (
          <p
            // eslint-disable-next-line react/no-array-index-key
            key={`confirm-line-${index}`}
            className={
              isV2
                ? "m-0 mb-2 text-[14px] leading-relaxed text-[#374151]"
                : "text-[#475569] text-sm text-left mb-2"
            }
          >
            {line}
          </p>
        ))}

        {children}

        <div
          className={
            isV2
              ? "mt-6 flex flex-wrap items-center justify-center gap-3"
              : "flex justify-between gap-4"
          }
        >
          <button
            onClick={onClose}
            disabled={loading}
            className={
              isV2
                ? `min-h-[44px] rounded-[10px] border border-[#E5E7EB] bg-white px-[18px] text-[14px] font-semibold text-[#374151] hover:bg-[#F9FAFB] ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`
                : `px-4 py-2 w-1/2 border rounded-lg hover:bg-gray-50 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`
            }
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
            className={
              isV2
                ? `min-h-[44px] rounded-[10px] px-[18px] text-[14px] font-semibold text-white ${
                    loading || confirmDisabled ? "opacity-50 cursor-not-allowed" : ""
                  } ${confirmButtonClass}`
                : `px-4 py-2 w-1/2 rounded-lg text-white ${
                    loading || confirmDisabled ? "opacity-50 cursor-not-allowed" : ""
                  } ${confirmButtonClass}`
            }
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Processing...
              </div>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
